import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

interface Props {
    /** Panelin altına açıldığı düğme. */
    anchor: HTMLElement
    onClose: () => void
    width?: number
    children: React.ReactNode
}

/**
 * Kolon filtresinin açılır paneli. Tablo çerçevesi taşan içeriği kestiği için
 * panel sayfanın en üst katmanına (body) çizilir ve düğmenin konumuna yerleşir.
 * Dışarı tıklanınca, Esc'ye basılınca, tablo kaydırılınca ya da pencere
 * boyutu değişince kapanır.
 */
const ColumnFilterPopover = ({ anchor, onClose, width, children }: Props) => {
    const ref = useRef<HTMLDivElement>(null)
    const [position, setPosition] = useState<{ left: number, top: number }>({ left: -9999, top: -9999 })

    // İçerik yüklendikçe yükseklik değişebilir; her çizimde konum yeniden hesaplanır.
    useLayoutEffect(() => {
        const el = ref.current

        if (!el)
            return

        const anchorRect = anchor.getBoundingClientRect()
        const w = el.offsetWidth
        const h = el.offsetHeight

        const left = Math.max(8, Math.min(anchorRect.left, window.innerWidth - w - 8))
        let top = anchorRect.bottom + 4

        // Aşağıda yer yoksa düğmenin üstüne açılır.
        if (top + h > window.innerHeight - 8)
            top = Math.max(8, anchorRect.top - h - 4)

        if (left != position.left || top != position.top)
            setPosition({ left, top })
    })

    useEffect(() => {
        const onMouseDown = (e: MouseEvent) => {
            const target = e.target as Node

            if (ref.current && !ref.current.contains(target) && !anchor.contains(target))
                onClose()
        }

        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key == 'Escape')
                onClose()
        }

        // Panelin kendi listesinin kaydırılması kapatmaz; tablonun ya da sayfanın kaydırılması kapatır.
        const onScroll = (e: Event) => {
            if (ref.current && ref.current.contains(e.target as Node))
                return

            onClose()
        }

        document.addEventListener('mousedown', onMouseDown)
        document.addEventListener('keydown', onKeyDown)
        document.addEventListener('scroll', onScroll, true)
        window.addEventListener('resize', onClose)

        return () => {
            document.removeEventListener('mousedown', onMouseDown)
            document.removeEventListener('keydown', onKeyDown)
            document.removeEventListener('scroll', onScroll, true)
            window.removeEventListener('resize', onClose)
        }
    }, [anchor, onClose])

    return createPortal(
        <div
            ref={ref}
            className='kf-popover'
            style={{ left: position.left, top: position.top, width: width }}
            role='dialog'
        >
            {children}
        </div>,
        document.body
    )
}

export default ColumnFilterPopover
